import { Directive, HostListener, inject, Input } from '@angular/core';
import { GoogleAnalyticsService } from '../services';

/**
 * Directive to track Google Analytics click events
 *
 * Works with both native HTML elements and Angular components that emit custom events.
 *
 * Usage:
 * - On native buttons: <button [lxGAClickEvent]="eventName">
 * - On lx-button: <lx-button [lxGAClickEvent]="eventName">
 * - On links: <a [lxGAClickEvent]="eventName">
 *
 * The directive listens to both:
 * - 'click' events (native DOM clicks)
 * - 'buttonClick' events (lx-button custom event)
 */
@Directive({
  standalone: true,
  selector: '[lxGAClickEvent]',
})
export class GoogleAnalyticsClickEventDirective {
  @Input() lxGAClickEvent: string | undefined;

  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private eventTracked = false;

  /**
   * Listens to buttonClick custom events from lx-button component
   * Handles: <lx-button> components that emit (buttonClick) output
   * Priority: Listen to this first to prevent double-tracking with native click events for lx-button
   */
  @HostListener('buttonClick', ['$event'])
  onButtonClick(event: MouseEvent): void {
    // Mark that we've tracked this event to prevent native click handler from firing
    this.eventTracked = true;
    this.trackEvent();
  }

  /**
   * Listens to native DOM click events
   * Handles: native <button>, <a>, <div>, etc.
   * Note: Skipped for lx-button since buttonClick event is handled above
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    // Only track if this isn't a lx-button (which handles tracking via buttonClick)
    if (!this.eventTracked) {
      this.trackEvent();
    }
    // Reset the flag for the next interaction
    this.eventTracked = false;
    event.stopImmediatePropagation();
  }

  /**
   * Tracks the Google Analytics event
   * Shared logic for both click and buttonClick handlers
   * @private
   */
  private trackEvent(): void {
    if (!this.lxGAClickEvent) {
      return;
    }

    this.googleAnalyticsService.trackClickEvent(this.lxGAClickEvent);
  }
}
